/**
 * Copy for the staged-product remediation panel (Alex / admin).
 * Keep dispatcher cadence aligned with
 * `scheduledRemediationDispatcher` (`every 15 minutes`).
 */

export const REMEDIATION_DISPATCHER_EVERY_MINUTES = 15;

export const REMEDIATION_ACTION_LABELS: Record<string, string> = {
  identifier_enrichment: 'Identifier enrichment',
  bh_fill: '3P fill (B&H)',
  catalog_extract: 'Catalog extract',
  snapshot_parse: 'Snapshot parse',
  content_rescrape: 'Content re-scrape',
  ai_generate_short: 'AI short description',
  ai_generate_description: 'AI description',
  ai_generate_features: 'AI features',
  ai_accept: 'Accept AI draft',
  ai_classify: 'AI classify',
  external: 'External write (scrape / edit / fill)',
};

const GAP_LABELS: Record<string, string> = {
  sku: 'SKU',
  upc: 'UPC/EAN',
  category: 'Category',
  productType: 'Product type',
  price: 'Price',
  weight: 'Weight',
  photo: 'Photo',
  shortDescription: 'Short description',
  longDescription: 'Description',
  features: 'Features',
  fujifilmCatalog: 'Fujifilm catalog',
  identifierReview: 'Identifier review',
  verificationError: 'Verification error',
  syntheticSku: 'Synthetic SKU',
  aiContentPending: 'Unaccepted AI content',
  documentation: 'Documentation',
  videos: 'Videos',
  inTheBox: 'In the box',
};

/** Gaps that never send the product to Needs Review on their own. */
const ADVISORY_GAPS = new Set([
  'syntheticSku',
  'documentation',
  'videos',
  'inTheBox',
]);

export function remediationActionLabel(action?: string | null): string {
  if (!action) return '—';
  return REMEDIATION_ACTION_LABELS[action] || action;
}

export function remediationGapLabel(gap: string): string {
  return GAP_LABELS[gap] || gap;
}

/** Dispatcher order. `unlabeled` is a row whose ledger has no lane yet. */
export const REMEDIATION_LANE_ORDER = ['ready', 'fillable', 'copy', 'sourceable', 'unlabeled'] as const;

export const REMEDIATION_LANE_COPY: Record<string, { label: string; detail: string }> = {
  ready: {
    label: 'Ready to leave',
    detail: 'No automated work left. The next dispatcher run moves these to pending or Needs Review.',
  },
  fillable: {
    label: 'Free fill',
    detail: 'A catalog extract, a snapshot parse, or an AI draft acceptance is still free. These run 8 at a time, up to 1,000 a run. The rest continue on the next run.',
  },
  copy: {
    label: 'Copy only',
    detail: 'Only a description or features list is left.',
  },
  sourceable: {
    label: 'Needs a source',
    detail: 'Barcode, weight, photo, or another paid step. Daily caps are unchanged.',
  },
  unlabeled: {
    label: 'Not labeled',
    detail: 'The ledger has no lane yet. The backfill or the next write of this product adds one.',
  },
};

export function remediationLaneLabel(lane?: string | null): string {
  if (!lane) return REMEDIATION_LANE_COPY.unlabeled.label;
  return REMEDIATION_LANE_COPY[lane]?.label || lane;
}

export function remainingRemediationActions(state: {
  chain?: string[];
  nextAction?: string | null;
}): string[] {
  const chain = state.chain || [];
  const next = state.nextAction;
  if (!next) return [];
  const index = chain.indexOf(next);
  return index >= 0 ? chain.slice(index) : [next];
}

function formatClock(d: Date): string {
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelativeFrom(due: Date, now: Date): string {
  const diffMs = now.getTime() - due.getTime();
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / 60000);
  if (mins < 1) return diffMs >= 0 ? 'just now' : 'in a moment';
  if (mins < 60) return diffMs >= 0 ? `${mins}m ago` : `in ${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return diffMs >= 0 ? `${hours}h ago` : `in ${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 14) return diffMs >= 0 ? `${days}d ago` : `in ${days}d`;
  return formatClock(due);
}

export function describeGapWhen(due: Date | null, now = new Date()): {
  eligible: boolean;
  headline: string;
  detail: string;
} {
  const cadence = `The dispatcher runs every ${REMEDIATION_DISPATCHER_EVERY_MINUTES} minutes and takes the oldest eligible products first.`;
  if (!due) {
    return {
      eligible: false,
      headline: 'No retry scheduled',
      detail: cadence,
    };
  }
  const eligible = due.getTime() <= now.getTime();
  if (eligible) {
    return {
      eligible: true,
      headline: `Eligible since ${formatClock(due)} (${formatRelativeFrom(due, now)})`,
      detail: `This is not a completed retry. ${cadence}`,
    };
  }
  return {
    eligible: false,
    headline: `Eligible starting ${formatClock(due)} (${formatRelativeFrom(due, now)})`,
    detail: `Backoff gate only — the first dispatcher run after that time can pick it up. ${cadence}`,
  };
}

export function describeGapSteps(state: {
  chain?: string[];
  nextAction?: string | null;
  exhausted?: boolean;
}): { next: string; then: string | null; exhaustedNote: string | null } {
  if (state.exhausted) {
    return {
      next: 'No more automated steps',
      then: null,
      exhaustedNote: 'A person has to resolve this, or a later scrape / edit has to change the inputs.',
    };
  }
  const remaining = remainingRemediationActions(state).map(remediationActionLabel);
  if (remaining.length === 0) {
    return {
      next: 'No automated step',
      then: null,
      exhaustedNote: 'This gap has no machine chain.',
    };
  }
  const [next, ...rest] = remaining;
  return {
    next,
    then: rest.length ? rest.join(' → ') : null,
    exhaustedNote: null,
  };
}

export function describeProductPipeline(input: {
  stagingStatus?: string;
  nextAttemptAt?: Date | null;
  openGaps?: string[];
  gapStates?: Record<string, { exhausted?: boolean; nextAction?: string | null }>;
  now?: Date;
}): { stage: string; nextWork: string; after: string } {
  const now = input.now || new Date();
  const status = input.stagingStatus || '';
  const stageLabels: Record<string, string> = {
    remediating: 'Remediating — closing gaps',
    pending: 'Pending — ready for AI verify',
    verifying: 'Verifying',
    review: 'Needs review',
    approved: 'Approved',
    promoted: 'Promoted',
    rejected: 'Rejected',
  };
  const open = input.openGaps || [];
  const blockingOpen = open.filter((g) => !ADVISORY_GAPS.has(g));
  const runnable = blockingOpen
    .map((gap) => ({ gap, st: input.gapStates?.[gap] }))
    .find((row) => row.st && !row.st.exhausted && row.st.nextAction);

  let nextWork = 'No automated work queued.';
  if (runnable?.st?.nextAction) {
    const when = describeGapWhen(input.nextAttemptAt || null, now);
    nextWork = `${remediationActionLabel(runnable.st.nextAction)} for ${remediationGapLabel(runnable.gap)}. ${when.headline}.`;
  } else if (blockingOpen.some((g) => input.gapStates?.[g]?.exhausted)) {
    nextWork = 'Blocking gaps are exhausted — this product is waiting on a person.';
  }

  const after =
    status === 'review' || (blockingOpen.length > 0 && blockingOpen.every((g) => input.gapStates?.[g]?.exhausted))
      ? 'Stays in Needs Review until someone edits identifiers / content or a scrape changes the inputs.'
      : 'When blocking gaps close: Pending → Verifying → Approved. Advisory gaps (docs / videos / in the box) never hold that path.';

  return {
    stage: stageLabels[status] || (status ? status : 'Unknown stage'),
    nextWork,
    after,
  };
}

export interface RemediationBudgetLine {
  action: string;
  label: string;
  used: number;
  cap: number | null;
}

export interface RemediationQueueStatusCopy {
  severity: 'info' | 'success' | 'warning' | 'error';
  headline: string;
  detail: string;
  lines: Array<RemediationBudgetLine & { spent: boolean }>;
}

const AI_COPY_ACTIONS = ['ai_generate_description', 'ai_generate_short', 'ai_generate_features'];

function budgetSpent(line: RemediationBudgetLine | undefined): boolean {
  return !!line && line.cap != null && line.cap > 0 && line.used >= line.cap;
}

function relativeAgo(ms: number, nowMs: number): string {
  const mins = Math.max(0, Math.round((nowMs - ms) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** Same overdue window as the pipeline board: 1.5× cadence, at least 30 minutes. */
function remediationRunOverdue(lastRunMs: number, nowMs: number): boolean {
  const allowanceMs = Math.max(REMEDIATION_DISPATCHER_EVERY_MINUTES * 1.5, 30) * 60_000;
  return nowMs - lastRunMs > allowanceMs;
}

function capSentence(lines: RemediationQueueStatusCopy['lines']): string | null {
  const spent = lines.filter((line) => line.spent);
  if (spent.length === 0) return null;
  const named = spent.map((line) => `${line.label} ${line.used} of ${line.cap}`).join(', ');
  return `${named}. Spent caps resume after midnight UTC. Steps that do not use them still run.`;
}

/**
 * One sentence for the staged queue: is remediation running, idle, out of
 * today's lookup budget, or stopped.
 */
export function describeRemediationQueueStatus(input: {
  status?: string | null;
  message?: string | null;
  lastRunMs?: number | null;
  processed?: number | null;
  budgetSkips?: number | null;
  gapsClosed?: number | null;
  sentToReview?: number | null;
  advanced?: number | null;
  budgets?: RemediationBudgetLine[];
  nowMs?: number;
}): RemediationQueueStatusCopy {
  const nowMs = input.nowMs ?? Date.now();
  const lines = (input.budgets || []).map((line) => ({ ...line, spent: budgetSpent(line) }));
  const aiLines = lines.filter((line) => AI_COPY_ACTIONS.includes(line.action));
  const aiSpent = aiLines.some((line) => line.spent);
  const visible = lines.filter((line) => !AI_COPY_ACTIONS.includes(line.action) || line.action === 'ai_generate_description');
  const displayLines = visible.map((line) => (
    line.action === 'ai_generate_description' && aiSpent
      ? { ...line, label: 'AI copy', spent: true }
      : line
  ));
  const spentDetail = capSentence(displayLines);

  const lastRunMs = input.lastRunMs ?? null;
  if (lastRunMs == null) {
    return {
      severity: 'error',
      headline: 'Remediation has not reported a run.',
      detail: spentDetail || 'This queue is not being worked until the dispatcher records a run.',
      lines: displayLines,
    };
  }

  const ago = relativeAgo(lastRunMs, nowMs);
  if (input.status === 'error') {
    const reason = input.message ? ` ${input.message}` : '';
    return {
      severity: 'error',
      headline: `The last remediation run failed ${ago}.${reason}`,
      detail: spentDetail || 'The Remediating count stays where it is until a run finishes.',
      lines: displayLines,
    };
  }
  if (input.status === 'running') {
    return {
      severity: 'info',
      headline: 'Remediation is running now.',
      detail: spentDetail || 'The tab counts update when this run finishes.',
      lines: displayLines,
    };
  }
  if (remediationRunOverdue(lastRunMs, nowMs)) {
    return {
      severity: 'warning',
      headline: `Remediation last ran ${ago}.`,
      detail: `It is scheduled every ${REMEDIATION_DISPATCHER_EVERY_MINUTES} minutes, so this looks stopped. ${spentDetail || ''}`.trim(),
      lines: displayLines,
    };
  }
  if (input.status === 'completed_with_errors' || input.status === 'partial') {
    return {
      severity: 'warning',
      headline: `The last remediation run, ${ago}, finished with errors.`,
      detail: spentDetail || 'Some products failed to update. Check the pipeline board for the run details.',
      lines: displayLines,
    };
  }

  const processed = input.processed ?? 0;
  const gapsClosed = input.gapsClosed ?? 0;
  const sentToReview = input.sentToReview ?? 0;
  const advanced = input.advanced ?? 0;
  const skips = input.budgetSkips ?? 0;
  const movement = [
    gapsClosed > 0 ? `${gapsClosed} gaps closed` : null,
    sentToReview > 0 ? `${sentToReview} sent to Needs Review` : null,
    advanced > 0 ? `${advanced} moved forward` : null,
    skips > 0 ? `${skips} steps skipped for a daily cap` : null,
  ].filter(Boolean).join(', ');

  if (spentDetail) {
    const closedNothing = processed > 0 && gapsClosed + sentToReview + advanced === 0;
    return {
      severity: 'warning',
      headline: spentDetail,
      detail: closedNothing
        ? `The last run, ${ago}, touched ${processed} products and closed nothing${skips > 0 ? ` (${skips} steps skipped)` : ''}. The Remediating count stays where it is.`
        : processed > 0
          ? `The last run, ${ago}, processed ${processed} products${movement ? ` (${movement})` : ''}.`
          : `The last run, ${ago}, found nothing due. The Remediating count stays the same until a product's next attempt time.`,
      lines: displayLines,
    };
  }

  const leftTheQueue = gapsClosed + sentToReview + advanced;
  if (processed > 0 && leftTheQueue === 0) {
    return {
      severity: 'warning',
      headline: `Remediation ran ${ago} and closed nothing.`,
      detail: skips > 0
        ? `It touched ${processed} products and skipped ${skips} steps because a daily cap was spent or the B&H fill slot was already taken. The Remediating count stays where it is.`
        : `It touched ${processed} products and none of them left Remediating.`,
      lines: displayLines,
    };
  }

  if (processed > 0) {
    return {
      severity: 'success',
      headline: `Remediation ran ${ago} and processed ${processed} products.`,
      detail: movement
        ? `${movement}. The Remediating count drops when a product leaves the queue.`
        : 'The Remediating count drops when a product leaves the queue.',
      lines: displayLines,
    };
  }

  return {
    severity: 'info',
    headline: `Remediation ran ${ago} and found nothing due.`,
    detail: 'Products stay in Remediating until their next attempt time. An idle run leaves the tab count where it is.',
    lines: displayLines,
  };
}
